import { auth, type AuthUser } from "./auth";
import type { Artist, Banner, Composer, CrbtLibraryItem, HomeResponse, MtvMovie, MtvPlaylist, MtvShelf, Playlist, RadioStation, VideoComment } from "../types";

export const API_BASE = (import.meta.env.VITE_API_BASE_URL || "https://api.yours4u.com").replace(/\/$/, "");
const AUTH_PATHS = [
  "/api/auth/login/",
  "/api/auth/register/",
  "/api/auth/google-login/",
  "/api/auth/refreshtoken/",
  "/api/auth/atom-token/",
  "/api/auth/continue-as-guest/",
];

export class APIError extends Error {
  status: number;
  data: Record<string, unknown> | null;

  constructor(status: number, data: Record<string, unknown> | null) {
    super(String(data?.detail || data?.error || data?.message || `Request failed (${status})`));
    this.status = status;
    this.data = data;
  }
}

type RequestOptions = RequestInit & {
  publicFallback?: boolean;
  retrying?: boolean;
};

type TokenResponse = {
  access: string;
  refresh?: string;
};

type Paginated<T> = {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
};

let refreshRequest: Promise<string> | null = null;

async function parseResponse(response: Response) {
  if (response.status === 204) return null;
  const contentType = response.headers.get("content-type") || "";
  return contentType.includes("application/json")
    ? response.json()
    : response.text().then((text) => ({ message: text }));
}

async function refreshAccessToken() {
  if (refreshRequest) return refreshRequest;
  const refresh = auth.refresh();
  if (!refresh) throw new Error("No refresh token available");

  refreshRequest = fetch(`${API_BASE}/api/auth/refreshtoken/`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ refresh }),
  })
    .then(async (response) => {
      const data = await parseResponse(response) as TokenResponse | Record<string, unknown>;
      if (!response.ok || !data || typeof data !== "object" || !("access" in data)) {
        throw new APIError(response.status, data as Record<string, unknown>);
      }
      const tokens = data as TokenResponse;
      auth.saveTokens(tokens.access, tokens.refresh || refresh);
      return tokens.access;
    })
    .finally(() => {
      refreshRequest = null;
    });
  return refreshRequest;
}

function expireSession() {
  auth.clear();
  window.dispatchEvent(new CustomEvent("ym:auth-expired"));
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { publicFallback = false, retrying = false, ...init } = options;
  const token = auth.access();
  const isAuthPath = AUTH_PATHS.some((authPath) => path.startsWith(authPath));
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      Accept: "application/json",
      ...(init.body && !(init.body instanceof FormData) ? { "Content-Type": "application/json" } : {}),
      ...(token && !isAuthPath ? { Authorization: `Bearer ${token}` } : {}),
      ...init.headers,
    },
  });

  if (response.status === 401 && !retrying && !isAuthPath) {
    try {
      await refreshAccessToken();
      return request<T>(path, { ...options, retrying: true });
    } catch {
      expireSession();
      if (publicFallback) {
        return request<T>(path, { ...options, retrying: true });
      }
    }
  }

  const body = await parseResponse(response);
  if (!response.ok) throw new APIError(response.status, body as Record<string, unknown> | null);
  return body as T;
}

export type Package = {
  id: number;
  name: string;
  description: string | null;
  price: string;
  duration_days: number;
  is_active: boolean;
  product_code: string;
};

export type Subscription = {
  id: number;
  msisdn: string;
  product_code: string;
  status: string;
  last_action: string;
  next_renewal_date: string | null;
  client_transaction_id: string | null;
};

export type PlaylistPage = {
  count: number;
  next: string | null;
  previous: string | null;
  results: Playlist[];
};

export type PlaylistFeedParams = {
  scope?: "all" | "yours" | "public";
  mode?: "behavior" | "random" | "admin";
  type?: string | string[];
  limit?: number;
  offset?: number;
};

export type CrbtLibraryPage = {
  count: number;
  next: string | null;
  previous: string | null;
  results: CrbtLibraryItem[];
};

export type NotificationItem = {
  id: number;
  title: string;
  message: string;
  action_url: string | null;
  read: boolean;
  created_at: string | null;
};

export type LoginDevice = {
  device_id: string;
  device_name: string;
  device_type: string;
  os: string;
  browser: string;
  last_login: string | null;
  first_login: string | null;
  is_active: boolean;
};

export type UploadImageResponse = {
  success: boolean;
  image_url: string;
  image_file_path: string;
};

export interface BrowseItem {
  id: number;
  name: string;
  artist?: { id: number; name: string };
}

export interface BrowseSuggestion {
  id: number;
  title: string;
  artist: { id: number; name: string };
  thumbnail: string | null;
}

export interface BrowsePlaylistResponse {
  genres?: BrowseItem[];
  albums?: BrowseItem[];
  artists?: BrowseItem[];
  suggestions?: BrowseSuggestion[];
}

export interface SongItem {
  id: number;
  title: string;
  artist: { id: number; name: string };
  thumbnail: string | null;
  audio_url?: string;
  duration?: number;
}

export const api = {
  baseUrl: API_BASE,
  home: () => request<HomeResponse>("/api/v2/home/", { publicFallback: true }),
  uploadImage: (formData: FormData) =>
    request<UploadImageResponse>("/api/music/imageupload", {
      method: "POST",
      body: formData,
    }),
  browsePlaylist: (type: "albums" | "genres" | "artists") =>
    request<BrowsePlaylistResponse>(`/api/playlist-browse?type=${type}`),
  fetchSongsInfinite: ({
    pageParam = 1,
    type,
    id,
    search,
    limit = 20,
  }: {
    pageParam?: number;
    type?: string;
    id?: string | number;
    search?: string;
    limit?: number;
  }) => {
    const offset = (pageParam - 1) * limit;
    const params = new URLSearchParams({ offset: String(offset), limit: String(limit) });
    if (search) {
      params.set("search", search);
    } else if (type && id) {
      if (type === "albums" || type === "album") params.set("album", String(id));
      else if (type === "artists" || type === "artist") params.set("artist", String(id));
      else if (type === "genres" || type === "genre") params.set("genre", String(id));
    }
    return request<Paginated<SongItem>>(`/api/music/songs/?${params}`);
  },
  updatePlaylistSongs: (playlistId: number | string, songIds: number[]) =>
    request<Playlist>(`/api/music/playlists/${playlistId}/`, {
      method: "PATCH",
      body: JSON.stringify({ song_ids: songIds }),
    }),
  yourPlaylists: () => request<Playlist[]>("/api/user/user-playlist/"),
  playlistFeed: (params: PlaylistFeedParams = {}) => {
    const searchParams = new URLSearchParams();
    if (params.scope) searchParams.set("scope", params.scope);
    if (params.mode) searchParams.set("mode", params.mode);
    if (params.type) {
      const typeStr = Array.isArray(params.type) ? params.type.join(",") : params.type;
      searchParams.set("type", typeStr);
    }
    if (params.limit !== undefined) searchParams.set("limit", String(params.limit));
    if (params.offset !== undefined) searchParams.set("offset", String(params.offset));

    const queryString = searchParams.toString();
    return request<PlaylistPage>(`/api/v2/music/playlist-feed/${queryString ? `?${queryString}` : ""}`);
  },
  publicPlaylists: (offset = 0, limit = 6) =>
    api.playlistFeed({ scope: "public", mode: "admin", limit, offset }),
  moodPlaylists: (offset = 0, limit = 6) =>
    api.playlistFeed({ scope: "yours", limit, offset }),
  createPlaylist: (payload: { title: string; description?: string; privacy: "PUBLIC" | "PRIVATE"; image?: string; type?: string }) =>
    request<Playlist>("/api/music/playlists/", {
      method: "POST",
      body: JSON.stringify({ type: "CUSTOM", ...payload }),
    }),
  updatePlaylist: (id: number, payload: { title?: string; description?: string; privacy?: "PUBLIC" | "PRIVATE"; song_ids?: number[] }) =>
    request<Playlist>(`/api/music/playlists/${id}/`, { method: "PATCH", body: JSON.stringify(payload) }),
  deletePlaylist: (id: number) => request<void>(`/api/music/playlists/${id}/`, { method: "DELETE" }),
  addSongToPlaylist: (playlistId: number, songId: number) =>
    request<Record<string, unknown>>("/api/user/addsong-to-playlist/", {
      method: "POST",
      body: JSON.stringify({ playlist_id: playlistId, song_id: songId }),
    }),
  toggleLikeSong: (songId: number) =>
    request<{ added: boolean; playlist_id: number }>("/api/user/toggle-like-song/", {
      method: "POST",
      body: JSON.stringify({ song_id: songId }),
    }),
  checkLikedSong: (songId: number): Promise<{ is_liked?: boolean; isLiked?: boolean }> =>
    request<{ is_liked?: boolean; isLiked?: boolean }>(`/api/user/check-liked-song?song_id=${songId}`).catch(() => ({
      is_liked: false,
      isLiked: false,
    })),
  followedArtists: () => request<Artist[]>("/api/music/artist/followed/"),
  recentPlays: () => request<Paginated<{ id: number; song: number; played_at: string }>>("/api/music/user-song-plays/?limit=40"),
  recordSongPlay: (songId: number | string) => {
    const numericId = Number(songId);
    if (!numericId || Number.isNaN(numericId)) {
      return Promise.resolve(null);
    }
    return request<{ id?: number; song?: number; played_at?: string }>("/api/music/user-song-plays/", {
      method: "POST",
      headers: {
        "X-Client-Platform": "web",
      },
      body: JSON.stringify({ song: numericId }),
    });
  },
  song: (id: number) => request<Record<string, unknown>>(`/api/music/songs/${id}/`),
  requestLyrics: (payload: { song_id: number; title: "lyrics" | "chords" }) =>
    request<Record<string, unknown>>("/api/request-lyrics/", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  radioStations: () => request<{ results?: RadioStation[] } | RadioStation[]>("/api/radio/stations"),
  crbtLibrary: ({
    offset = 0,
    limit = 12,
    search = "",
    isFree,
  }: {
    offset?: number;
    limit?: number;
    search?: string;
    isFree?: boolean;
  } = {}) => {
    const params = new URLSearchParams({ offset: String(offset), limit: String(limit) });
    if (search) params.set("search", search);
    if (typeof isFree === "boolean") params.set("isFree", String(isFree));
    return request<CrbtLibraryPage>(`/api/music/crbt-library/?${params}`);
  },
  crbt: (id: string) => request<CrbtLibraryItem>(`/api/music/crbt-library/${id}/`),
  crbtStatus: (msisdn: string) =>
    request<unknown>(`/atom/api/crbt/status/?msisdn=${encodeURIComponent(msisdn)}&lang=en`),
  requestCrbtOtp: (msisdn: string) =>
    request<Record<string, unknown>>("/atom/api/crbt/generate-otp/", {
      method: "POST",
      body: JSON.stringify({ msisdn }),
    }),
  confirmCrbtPurchase: (payload: {
    msisdn: string;
    crbtlibrary_id: number;
    otp: string;
    has_crbt_subscription: boolean;
    clientTransactionId?: string;
  }) =>
    request<Record<string, unknown>>("/atom/api/crbt/purchase/confirm/", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  profile: async () => {
    const res = await request<{ user: AuthUser; profile?: Record<string, unknown> }>("/api/profile/me");
    if (res?.user) {
      const isPrem = Boolean(
        res.user.is_premium ||
        res.user.role === "premium" ||
        res.user.role === "vip" ||
        res.user.isAtomYathaUser ||
        (res.profile as any)?.is_premium ||
        (res.profile as any)?.is_subscribed ||
        (res.profile as any)?.subscribed ||
        (res.profile as any)?.has_active_subscription ||
        (res.user as any)?.is_subscribed ||
        (res.user as any)?.subscribed
      );
      const updatedUser: AuthUser = {
        ...auth.user(),
        ...res.user,
        is_premium: isPrem,
      };
      auth.saveUser(updatedUser);
      try {
        localStorage.setItem(
          "user_status",
          JSON.stringify({
            ...res.user,
            ...res.profile,
            is_premium: isPrem,
          })
        );
      } catch {}
    }
    return res;
  },
  updateProfile: (payload: Partial<Pick<AuthUser, "name" | "email" | "msisdn">>) =>
    request<{ user: AuthUser; profile?: Record<string, unknown> }>("/api/profile/me", {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  editProfile: (formData: FormData) =>
    request<{ user: AuthUser; profile?: Record<string, unknown> }>("/api/profile/me", {
      method: "PUT",
      body: formData,
    }),
  loginHistory: () => request<{ login_history: LoginDevice[]; total_devices: number }>("/api/auth/login-history/"),
  revokeDevice: (deviceId: string) =>
    request<{ status: string }>("/api/auth/devices/revoke/", {
      method: "POST",
      body: JSON.stringify({ device_id: deviceId }),
    }),
  logoutAllDevices: () =>
    request<{ status: string }>("/api/auth/devices/logoutall/", { method: "POST" }),
  notifications: (status = "all") =>
    request<{ notifications: NotificationItem[]; summary: { total: number; read: number; unread: number } }>(`/api/user/notifications/?status=${status}`),
  markNotificationsRead: (notificationIds?: number[]) =>
    request<Record<string, unknown>>("/api/user/notifications/", {
      method: "POST",
      body: JSON.stringify(notificationIds ? { notification_ids: notificationIds } : { all: true }),
    }),
  deleteNotifications: (notificationIds: number[]) =>
    request<Record<string, unknown>>("/api/user/notifications/", {
      method: "DELETE",
      body: JSON.stringify({ notification_ids: notificationIds }),
    }),
  login: (identifier: string, password: string) =>
    request<{ access: string; refresh: string; user: AuthUser }>("/api/auth/login/", {
      method: "POST",
      body: JSON.stringify({ identifier, password }),
    }),
  register: (payload: {
    name: string;
    username: string;
    email: string;
    msisdn: string;
    password: string;
  }) =>
    request<{ tokens?: TokenResponse; user?: AuthUser }>("/api/auth/register/", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  forgotPassword: (email: string) =>
    request<{ message?: string; detail?: string }>("/api/auth/forgot-password/", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
  changePassword: (oldPassword: string, newPassword: string) =>
    request<{ message?: string; detail?: string }>("/api/auth/change-password/", {
      method: "POST",
      body: JSON.stringify({ old_password: oldPassword, new_password: newPassword }),
    }),
  changeEmail: (newEmail: string, password: string) =>
    request<{ message?: string; detail?: string; user?: AuthUser }>("/api/auth/change-email/", {
      method: "POST",
      body: JSON.stringify({ new_email: newEmail, password }),
    }),
  changePhone: (newPhone: string, password: string) =>
    request<{ message?: string; detail?: string; user?: AuthUser }>("/api/auth/change-phone-number/", {
      method: "POST",
      body: JSON.stringify({ new_phone: newPhone, password }),
    }),
  getLoginHistory: () =>
    request<{ login_history: any[]; total_devices: number }>("/api/auth/login-history/"),
  checkUsernameAvailability: (username: string) =>
    request<{ available: boolean }>(`/api/auth/check-username/?username=${encodeURIComponent(username)}`),
  checkEmailAvailability: (email: string) =>
    request<{ available: boolean }>(`/api/auth/check-email/?email=${encodeURIComponent(email)}`),
  checkMsisdnAvailability: (msisdn: string) =>
    request<{ available: boolean }>(`/api/auth/check-msisdn/?msisdn=${encodeURIComponent(msisdn)}`),
  addPhoneNumber: (phoneNumber: string) =>
    request<{ message?: string; detail?: string; user?: AuthUser }>("/api/add-phone-number/", {
      method: "POST",
      body: JSON.stringify({ phone_number: phoneNumber }),
    }),
  addPassword: (newPassword: string) =>
    request<{ message?: string; detail?: string }>("/api/auth/add-password/", {
      method: "POST",
      body: JSON.stringify({ new_password: newPassword }),
    }),
  loginWithGoogle: (idToken: string) =>
    request<{ access: string; refresh: string; user: AuthUser }>("/api/auth/google-login/", {
      method: "POST",
      body: JSON.stringify({ id_token: idToken }),
    }),
  atomLogin: (token: string) =>
    request<{ user: AuthUser; tokens: TokenResponse }>("/api/auth/atom-token/", {
      method: "POST",
      body: JSON.stringify({ token }),
    }),
  atomTokenStatus: () => request<{ status: string }>("/atom/token-status/"),
  atomOAuthUrl: (returnTo: string) =>
    `${API_BASE}/atom/api/oauth/initiate/?return_to=${encodeURIComponent(returnTo)}`,
  packages: () => request<{ count: number; results: Package[] }>("/atom/packages/?is_active=true"),
  subscriptions: async (msisdn?: string) => {
    let cleanMsisdn = msisdn;
    if (cleanMsisdn) {
      if (cleanMsisdn.startsWith("959")) {
        cleanMsisdn = "0" + cleanMsisdn.slice(2);
      }
      cleanMsisdn = parseInt(cleanMsisdn, 10).toString();
    }
    const res = await request<Subscription[]>(
      cleanMsisdn ? `/atom/api/subscription/status/user/?msisdn=${encodeURIComponent(cleanMsisdn)}` : "/atom/api/subscription/status/user/"
    );
    if (Array.isArray(res)) {
      try {
        localStorage.setItem("subscriptionStatus", JSON.stringify(res));
      } catch {}
      const hasActive = res.some((s) => s.status === "active");
      if (hasActive) {
        const u = auth.user();
        if (u) {
          auth.saveUser({ ...u, is_premium: true });
        } else {
          try {
            localStorage.setItem("user_status", JSON.stringify({ is_premium: true }));
          } catch {}
          if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("ym_quota_updated"));
          }
        }
      }
    }
    return res;
  },
  audioLibraryPurchases: () => request<{ purchases: any[] | any }>("/atom/api/audiolibrary-purchase/"),
  requestSubscriptionOtp: (msisdn: string, productCode: string) =>
    request<{ message: string; clientTransactionId: string }>("/atom/api/purchase/initiate/", {
      method: "POST",
      body: JSON.stringify({ msisdn, productCode }),
    }),
  confirmSubscriptionOtp: (
    msisdn: string,
    transactionPin: string,
    clientTransactionId: string,
    productCode: string,
  ) =>
    request<{ message: string }>("/atom/api/purchase/confirm/", {
      method: "POST",
      body: JSON.stringify({ msisdn, transactionPin, clientTransactionId, productCode }),
    }),
  requestUnsubscribeOtp: (msisdn: string, productCode: string) =>
    request<{ message: string; clientTransactionId: string }>("/atom/api/subscription/unsubscribe/initiate/", {
      method: "POST",
      body: JSON.stringify({ msisdn, productCode }),
    }),
  confirmUnsubscribeOtp: (
    msisdn: string,
    transactionPin: string,
    clientTransactionId: string,
    productCode: string,
  ) =>
    request<{ message: string; status?: string }>("/atom/api/subscription/unsubscribe/confirm/", {
      method: "POST",
      body: JSON.stringify({ msisdn, transactionPin, clientTransactionId, productCode }),
    }),
  initiateAudioLibraryPurchaseAtom: (data: { msisdn: string; audiolibrary_id: number; clientTransactionId: string }) =>
    request<{ message: string; clientTransactionId?: string; msisdn?: string; audiolibrary_id?: number }>("/atom/api/purchase/audio/initiate/", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  confirmAudioLibraryPurchaseAtom: (data: { msisdn: string; audiolibrary_id: number; transactionPin: string; clientTransactionId: string }) =>
    request<{ message?: string; status?: string; audiolibrary_id?: number }>("/atom/api/purchase/audio/confirm/", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  ayaPay: (payload: { amount: string; product_code: string; payment_type: "PUSH_PAY" | "QR_CODE"; customerPhone?: string }) =>
    request<any>("/subscription/ayapay/", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  generateCRBTOtp: (msisdn: string) =>
    request<any>("/atom/api/crbt/generate-otp/", {
      method: "POST",
      body: JSON.stringify({ msisdn }),
    }),
  deleteCRBTTone: (payload: { msisdn: string; tune_id: number; tune_name: string; otp: string; clientTransactionId?: string }) =>
    request<any>("/atom/api/crbt/delete-tones/", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  deleteCRBTSubscription: (payload: { msisdn: string; otp: string; clientTransactionId?: string }) =>
    request<any>("/atom/api/crbt/delete-subscription/", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  getPurchasedCRBTTones: (msisdn?: string, lang: string = "en") =>
    request<any[]>(`/atom/api/crbt/purchased-tones/?${new URLSearchParams({ ...(msisdn ? { msisdn } : {}), lang }).toString()}`),
  createMmgbTransaction: async (payload: { amount: number; description: string }) => {
    const res = await fetch("https://pg.mmgbpay.com/api/h5/create-transaction/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer 50663278-1b7b-4e06-a59e-2718dc82c163",
      },
      body: JSON.stringify(payload),
    });
    return res.json() as Promise<{ payment_url?: string; [key: string]: any }>;
  },
  search: (query: string) =>
    request<Record<string, unknown>>(`/api/music/search/${encodeURIComponent(query)}`),
  playlist: (id: string) => request<Record<string, unknown>>(`/api/music/playlists/${id}/`),
  album: (id: string) => request<Record<string, unknown>>(`/api/music/albums/${id}/`),
  artist: (id: string) => request<Record<string, unknown>>(`/api/music/artists/${id}`),
  artistFollowStatus: (id: number) =>
    request<{ artist_id: number; following: boolean }>(`/api/music/artist/follow/check?artist_id=${id}`),
  toggleArtistFollow: (id: number) =>
    request<{ detail: "followed" | "unfollowed" }>("/api/music/artist/follow/", {
      method: "POST",
      body: JSON.stringify({ artist_id: id }),
    }),
  submitReport: (payload: {
    item_id: number;
    item_type: "song" | "artist" | "playlist" | "mtv";
    reason: string;
    details?: string;
  }) =>
    request<Record<string, unknown>>("/api/user/reports/", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  mtvShelves: (offset = 0, limit = 20) =>
    request<Paginated<MtvShelf>>(`/api/video/mtvpageshelves/?limit=${limit}&offset=${offset}`),
  mtvMovies: (offset = 0, limit = 18) =>
    request<Paginated<MtvMovie>>(`/api/video/movies/?limit=${limit}&offset=${offset}`),
  mtvMovie: (id: number) => request<MtvMovie>(`/api/video/movies/${id}/`),
  addMtvView: (id: number) =>
    request<Record<string, unknown>>(`/api/user/mtvmovie/${id}/view/`, { method: "POST" }),
  toggleMtvLike: (id: number) =>
    request<{ liked: boolean; likes_count?: number }>(`/api/user/mtvmovie/${id}/like/`, { method: "POST" }),
  mtvPlaylists: () => request<MtvPlaylist[]>("/api/user/mtv-playlists/"),
  addMtvToPlaylist: (playlistId: number, movieId: number) =>
    request<Record<string, unknown>>(`/api/user/mtv-playlists/${playlistId}/add-movie/`, {
      method: "POST",
      body: JSON.stringify({ movie_id: movieId }),
    }),
  createMtvPlaylistWithMovie: (title: string, visibility: "public" | "private", movieId: number) =>
    request<MtvPlaylist>("/api/user/mtv-playlists/create-with-movie/", {
      method: "POST",
      body: JSON.stringify({ title, visibility, movie_id: movieId }),
    }),
  videoComments: (movieId: number, limit = 1) =>
    request<Paginated<VideoComment>>(`/api/video/comments/?movie=${movieId}&limit=${limit}`),
  getVideoComments: (movieId: number, limit?: number) => {
    const query = limit ? `?movie=${movieId}&limit=${limit}` : `?movie=${movieId}`;
    return request<Paginated<VideoComment>>(`/api/video/comments/${query}`);
  },
  postVideoComment: (payload: { movie: number; content: string; parent?: number }) =>
    request<VideoComment>("/api/video/comments/", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  editVideoComment: (commentId: number, content: string) =>
    request<VideoComment>(`/api/video/comments/${commentId}/`, {
      method: "PATCH",
      body: JSON.stringify({ content }),
    }),
  deleteVideoComment: (commentId: number) =>
    request<{ detail?: string }>(`/api/video/comments/${commentId}/`, {
      method: "DELETE",
    }),
  continueAsGuest: () =>
    request<{ tokens: TokenResponse; user: AuthUser }>("/api/auth/continue-as-guest/", {
      method: "POST",
    }),
  getArtists: (params: {
    isLocal?: boolean;
    gender?: string;
    offset?: number;
    limit?: number;
    order_type?: "home" | "artist" | "trending" | "top" | "newbies" | "group";
  }) => {
    const query = new URLSearchParams();
    if (params.isLocal !== undefined) query.set("isLocal", params.isLocal ? "true" : "false");
    if (params.gender) query.set("gender", params.gender);
    if (params.offset !== undefined) query.set("offset", params.offset.toString());
    if (params.limit !== undefined) query.set("limit", params.limit.toString());
    if (params.order_type) query.set("order_type", params.order_type);
    return request<Paginated<Artist>>(`/api/music/artists/?${query.toString()}`);
  },
  getComposers: (params: {
    isLocal?: boolean;
    gender?: string;
    offset?: number;
    limit?: number;
  }) => {
    const query = new URLSearchParams();
    if (params.isLocal !== undefined) query.set("isLocal", params.isLocal ? "true" : "false");
    if (params.gender) query.set("gender", params.gender);
    if (params.offset !== undefined) query.set("offset", params.offset.toString());
    if (params.limit !== undefined) query.set("limit", params.limit.toString());
    return request<Paginated<Composer>>(`/api/music/composers/?${query.toString()}`);
  },
  crbtCategories: () => request<{ id: number | null; name: string; is_system: boolean }[]>("/api/music/crbt/public/categories-list").then(cats => cats.filter((category: any) => !category.is_system)),
  crbtItems: (params: { category_id?: number | null; page?: number; page_size?: number }) => {
    const searchParams = new URLSearchParams();
    if (params.category_id !== undefined && params.category_id !== null) {
      searchParams.set("category_id", String(params.category_id));
    }
    if (params.page !== undefined) searchParams.set("page", String(params.page));
    if (params.page_size !== undefined) searchParams.set("page_size", String(params.page_size));
    return request<any>(`/api/music/crbt/public/items?${searchParams}`);
  },
  musicBoxSongs: () => request<{ results: any[] }>("/api/music/musicboxsongs/"),
  getAudioLibrary: (params: {
    search?: string;
    singer?: string;
    offset?: number;
    limit?: number;
    type?: string;
    genre?: string;
    mood?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params.search) searchParams.set("search", params.search);
    if (params.singer) searchParams.set("singer", params.singer);
    if (params.offset !== undefined) searchParams.set("offset", String(params.offset));
    if (params.limit !== undefined) searchParams.set("limit", String(params.limit));
    if (params.type) searchParams.set("type", params.type);
    if (params.genre) searchParams.set("genre", params.genre);
    if (params.mood) searchParams.set("mood", params.mood);
    return request<any>(`/api/music/audio-library/?${searchParams}`);
  },
  getGenres: () => request<any[]>("/api/genres"),
  getMoods: () => request<any[]>("/api/moods"),
  banners: (type?: string) => {
    const searchParams = new URLSearchParams();
    if (type) searchParams.set("type", type);
    return request<{ results: Banner[] }>(`/api/banner?${searchParams}`).then(res => res.results || []);
  },
  getAudioLibraryDetail: (id: string | number) => request<any>(`/api/music/audio-library/${id}`),
  toggleLikeAudioLibrary: (songId: number) =>
    request<{ added: boolean }>(`/api/user/toggle-like-audiolibrary/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ audiolibrary_id: songId }),
    }),
  getLikedAudioLibrarySongs: () => request<any>(`/api/user/liked-audiolibrary/`),
  toggleLikeCRBT: (crbtId: number) =>
    request<{ liked: boolean }>(`/api/user/toggle-like-crbt/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ crbt_id: crbtId }),
    }),
  getLikedCRBTs: () => request<any>(`/api/user/liked-crbt/`),
  getAudioLibraryPurchaseStatus: (songId: number | string) =>
    request<any>(`/atom/api/audiolibrary-purchase/?audiolibrary_id=${songId}`),
  initiateAudioLibraryPurchase: (songId: number) =>
    request<any>(`/atom/api/audiolibrary-purchase/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ audiolibrary_id: songId }),
    }),
  cancelAudioLibraryPurchase: (purchaseId: number) =>
    request<any>(`/atom/api/audiolibrary-purchase/?purchase_id=${purchaseId}`, {
      method: "DELETE",
    }),
  getUploadedSongs: () => request<{ songs: any[] }>("/api/user/songs/"),
  uploadSongToLibrary: (formData: FormData) =>
    request<any>("/api/user/songs/upload/", {
      method: "POST",
      body: formData,
    }),
  submitNoiseChallenge: async (dbLevel: number): Promise<{
    status: "success" | "failure";
    message: string;
    prize_name?: string;
    expiry_date?: string;
    peak_db?: number;
  }> => {
    try {
      const user = auth.user();
      let msisdn = user?.msisdn;
      if (!msisdn) {
        const profile = await api.profile().catch(() => null);
        msisdn = (profile as any)?.user?.msisdn || (profile as any)?.profile?.msisdn;
      }
      if (!msisdn) {
        throw new Error("User phone number not found. Please log in.");
      }
      return await request<{
        status: "success" | "failure";
        message: string;
        prize_name?: string;
        expiry_date?: string;
        peak_db?: number;
      }>(`/api/noise-challenge/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ msisdn, db_level: dbLevel }),
      });
    } catch (err) {
      console.error("Failed to submit noise challenge:", err);
      return {
        status: "failure",
        message: err instanceof Error ? err.message : "Failed to submit challenge",
      };
    }
  },
};

export const authTesting = {
  refreshAccessToken,
  resetRefreshRequest: () => {
    refreshRequest = null;
  },
};
